import { useCallback, useEffect, useRef } from 'react'

import { ingestEvents, type PlayEvent } from '../../../api/drama'
import { platform } from '../../../lib/telegram'

/** 心跳间隔。口径见 docs/项目开发规范.md，改动会让前后数据不可比。 */
const HEARTBEAT_SECONDS = 20
const FLUSH_SECONDS = 10
const MAX_BUFFER = 40

type Milestone = 'progress_25' | 'progress_50' | 'progress_75' | 'progress_95'

const MILESTONES: Array<[number, Milestone]> = [
  [0.25, 'progress_25'],
  [0.5, 'progress_50'],
  [0.75, 'progress_75'],
  [0.95, 'progress_95'],
]

/**
 * 播放埋点。选品数据的唯一来源，所以宁可多发也不能漏。
 *
 * 心跳必须带播放位置 —— 只记开始和结束算不出真实观看时长，
 * 更算不出弃剧点在第几秒。
 */
export function useTelemetry(source?: string) {
  const buffer = useRef<PlayEvent[]>([])
  const anonId = useRef<string>('')
  const lastHeartbeat = useRef(0)
  const reached = useRef(new Set<Milestone>())
  /** 最近一次看到的解码帧高度。每条埋点都带上，用来统计档位分布。 */
  const videoHeight = useRef(0)

  if (!anonId.current) {
    const key = 'drama.anonId'
    anonId.current = localStorage.getItem(key) ?? crypto.randomUUID()
    localStorage.setItem(key, anonId.current)
  }

  /** 返回的 Promise 在这一批发完（成败都算）时完成，要等服务端记上再读的地方用。 */
  const flush = useCallback((): Promise<void> => {
    if (buffer.current.length === 0) return Promise.resolve()
    const batch = buffer.current
    buffer.current = []
    // 埋点失败不能影响播放，静默丢弃即可
    return ingestEvents(batch).then(
      () => {},
      () => {},
    )
  }, [])

  const push = useCallback(
    (event: PlayEvent['event'], sessionId: string, episodeId: number, position: number) => {
      buffer.current.push({
        sessionId,
        episodeId,
        event,
        positionSeconds: Math.max(0, Math.floor(position)),
        source,
        platform: platform(),
        anonId: anonId.current,
        // 还没拿到就不带这个字段——填 0 会把档位分布污染成一堆假的最低档
        videoHeight: videoHeight.current || undefined,
      })
      if (buffer.current.length >= MAX_BUFFER) flush()
    },
    [flush, source],
  )

  /** 切集时重置里程碑，否则第二集不会再上报进度事件。 */
  const resetEpisode = useCallback(() => {
    reached.current.clear()
    lastHeartbeat.current = 0
    videoHeight.current = 0
  }, [])

  /** 由播放器的 timeupdate 驱动，内部自行判断该不该发。 */
  const onProgress = useCallback(
    (sessionId: string, episodeId: number, position: number, duration: number, height: number) => {
      // 自适应码率会在播放中途来回切，所以每次都更新，不是只取起播那一下
      if (height > 0) videoHeight.current = height
      if (position - lastHeartbeat.current >= HEARTBEAT_SECONDS) {
        lastHeartbeat.current = position
        push('heartbeat', sessionId, episodeId, position)
      }
      if (duration <= 0) return
      const ratio = position / duration
      for (const [threshold, name] of MILESTONES) {
        if (ratio >= threshold && !reached.current.has(name)) {
          reached.current.add(name)
          push(name, sessionId, episodeId, position)
        }
      }
    },
    [push],
  )

  useEffect(() => {
    const timer = window.setInterval(flush, FLUSH_SECONDS * 1000)
    // 用户直接关掉 Mini App 时，pagehide 是最后的机会
    const onHide = () => flush()
    window.addEventListener('pagehide', onHide)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('pagehide', onHide)
      flush()
    }
  }, [flush])

  return { push, onProgress, resetEpisode, flush }
}
